import { useEffect, useState, useRef } from "react";
import { ArrowClockwise, ChatCircleDots, PaperPlaneRight, Paperclip, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, API, formatApiError as formatDetail } from "../../lib/api";

export default function Messages() {
  const [convs, setConvs] = useState(null);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const attach = async (file) => {
    if (!file || !active) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/conversations/${active}/uploads`, fd);
      toast.success(`${file.name} sent — the client has been emailed.`);
      await openThread(active);
    } catch (e) {
      toast.error(formatDetail(e) || "Upload failed — try a smaller file.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const load = () => {
    api.get("/conversations").then((r) => setConvs(r.data)).catch(() => toast.error("Could not load conversations."));
  };

  useEffect(() => {
    load();
  }, []);

  const openThread = async (token) => {
    setActive(token);
    const { data } = await api.get(`/conversations/${token}`);
    setThread(data);
  };

  const sendReply = async (e) => {
    e.preventDefault();
    const text = reply.trim();
    if (!text || !active) return;
    setSending(true);
    try {
      await api.post(`/conversations/${active}/messages`, { body: text });
      setReply("");
      await openThread(active);
      load();
      toast.success("Reply sent — the client has been emailed a link to your answer.");
    } catch {
      toast.error("Could not send reply. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="admin-messages">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-primary tracking-tight">Client messages</h1>
          <p className="mt-2 text-sm text-foreground/60" data-testid="admin-messages-count">
            {convs ? `${convs.length} portal thread${convs.length === 1 ? "" : "s"}` : "Loading…"}
          </p>
        </div>
        <button
          data-testid="admin-messages-refresh"
          onClick={() => { load(); if (active) openThread(active); }}
          className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors duration-300"
        >
          <ArrowClockwise size={15} weight="bold" /> Refresh
        </button>
      </div>

      <div className="mt-10 grid lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-4 space-y-3" data-testid="admin-conversations-list">
          {convs && convs.length === 0 && (
            <div className="bg-card border border-border rounded-sm px-8 py-14 text-center" data-testid="admin-conversations-empty">
              <ChatCircleDots size={34} weight="duotone" className="text-muted-foreground mx-auto" />
              <p className="mt-4 text-sm text-foreground/50 font-medium">No portal threads yet — they're created automatically when someone pays.</p>
            </div>
          )}
          {convs &&
            convs.map((c) => (
              <button
                key={c.token}
                data-testid={`admin-conversation-row-${c.token}`}
                onClick={() => openThread(c.token)}
                className={`w-full text-left bg-card border rounded-sm px-6 py-5 transition-colors duration-300 ${
                  active === c.token ? "border-primary" : "border-border hover:border-accent/60"
                }`}
              >
                <p className="font-semibold text-foreground text-sm flex items-center gap-2">
                  {c.client_name || c.client_email}
                  {c.admin_unread > 0 && (
                    <span className="bg-accent text-accent-foreground text-[10px] font-bold rounded-full min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center" data-testid={`admin-unread-badge-${c.token}`}>
                      {c.admin_unread}
                    </span>
                  )}
                </p>
                <p className="text-xs text-foreground/50 mt-1">
                  {c.plan} · {new Date(c.last_activity).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </p>
              </button>
            ))}
        </div>

        <div className="lg:col-span-8 bg-card border border-border rounded-sm p-7 md:p-9 min-h-[300px]" data-testid="admin-thread">
          {!thread ? (
            <p className="text-sm text-foreground/45 py-16 text-center" data-testid="admin-thread-placeholder">
              Select a thread to read and reply.
            </p>
          ) : (
            <>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground" data-testid="admin-thread-title">
                {thread.client_name || "Client"} · {thread.plan}
              </p>
              <div className="mt-6 space-y-4 max-h-[420px] overflow-y-auto pr-2" data-testid="admin-thread-messages">
                {thread.messages.length === 0 && (
                  <p className="text-sm text-foreground/50">The client hasn't written yet. Their first question will appear here.</p>
                )}
                {thread.messages.map((m, i) => (
                  <div key={i} className={`flex ${m.from === "admin" ? "justify-end" : "justify-start"}`} data-testid={`admin-thread-message-${i}`}>
                    <div
                      className={`max-w-[85%] rounded-sm px-5 py-4 text-sm leading-relaxed ${
                        m.from === "admin" ? "bg-primary text-primary-foreground" : "bg-secondary/70 border border-border text-foreground"
                      }`}
                    >
                      {m.body && <p className="whitespace-pre-wrap">{m.body}</p>}
                      {m.attachment && (
                        <a
                          data-testid={`admin-thread-attachment-${i}`}
                          href={`${API}/files/${m.attachment.path}`}
                          target="_blank"
                          rel="noreferrer"
                          className={`flex items-center gap-2.5 rounded-sm px-4 py-3 text-sm font-semibold border transition-colors duration-300 ${
                            m.from === "admin"
                              ? "border-primary-foreground/25 text-primary-foreground hover:border-accent"
                              : "border-border text-foreground hover:border-accent"
                          }`}
                        >
                          <FileText size={18} weight="duotone" className="shrink-0" />
                          {m.attachment.name}
                          <span className="text-[10px] opacity-60">({Math.round(m.attachment.size / 1024)} KB)</span>
                        </a>
                      )}
                      <p className={`mt-2 text-[10px] font-bold uppercase tracking-wider ${m.from === "admin" ? "text-primary-foreground/50" : "text-muted-foreground"}`}>
                        {m.from === "admin" ? "You" : "Client"} · {new Date(m.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <form onSubmit={sendReply} className="mt-6" data-testid="admin-reply-form">
                <textarea
                  data-testid="admin-reply-input"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  rows={3}
                  placeholder="Write your reply — the client is emailed a link to read it…"
                  className="w-full bg-transparent border border-border rounded-sm px-4 py-3.5 text-sm font-medium text-foreground placeholder:text-foreground/35 focus:outline-none focus:border-primary transition-colors duration-300 resize-none"
                />
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.doc,.docx"
                    className="hidden"
                    data-testid="admin-file-input"
                    onChange={(e) => attach(e.target.files?.[0])}
                  />
                  <button
                    type="button"
                    data-testid="admin-attach-button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="flex items-center gap-2 border border-border text-foreground/70 px-5 py-3 rounded-full font-semibold text-sm hover:border-primary hover:text-primary disabled:opacity-50 transition-colors duration-300"
                  >
                    <Paperclip size={14} weight="bold" />
                    {uploading ? "Uploading…" : "Attach"}
                  </button>
                  <button
                    data-testid="admin-reply-send"
                    type="submit"
                    disabled={sending || !reply.trim()}
                    className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-semibold text-sm hover:bg-accent disabled:opacity-50 transition-colors duration-300"
                  >
                    {sending ? "Sending…" : "Send reply"}
                    <PaperPlaneRight size={15} weight="bold" />
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
